<template>
    <div class="kb-structure-editor">
        <div
            v-for="(topic, ti) in topics"
            :key="topic.key"
            class="kb-structure-editor__topic"
        >
            <div class="kb-structure-editor__topic-head">
                <input
                    class="input kb-structure-editor__topic-input"
                    type="text"
                    v-model="topic.title"
                    :placeholder="$t('knowledgebase.structureTopicPlaceholder')"
                    @input="emit"
                />
                <button
                    class="button is-small is-white kb-structure-editor__remove"
                    :title="$t('knowledgebase.structureRemoveTopic')"
                    @click="removeTopic(ti)"
                >✕</button>
            </div>

            <div class="kb-structure-editor__articles">
                <div
                    v-for="(article, ai) in topic.articles"
                    :key="article.key"
                    class="kb-structure-editor__article"
                >
                    <span class="kb-structure-editor__bullet">•</span>
                    <input
                        class="input is-small kb-structure-editor__article-input"
                        type="text"
                        v-model="article.title"
                        :placeholder="$t('knowledgebase.structureArticlePlaceholder')"
                        @input="emit"
                    />
                    <button
                        class="button is-small is-white kb-structure-editor__remove"
                        :title="$t('knowledgebase.structureRemoveArticle')"
                        @click="removeArticle(ti, ai)"
                    >✕</button>
                </div>
                <button
                    class="button is-small is-text kb-structure-editor__add-article"
                    @click="addArticle(ti)"
                >+ {{ $t('knowledgebase.structureAddArticle') }}</button>
            </div>
        </div>

        <button
            class="button is-small kb-structure-editor__add-topic"
            @click="addTopic"
        >+ {{ $t('knowledgebase.structureAddTopic') }}</button>
    </div>
</template>

<script>
// A two-level editable tree: topics, each with a list of article titles.
// Structure is unambiguous by construction — the only affordances are
// add/remove topic and add/remove article — so there is no parsing, no
// nesting beyond two levels, and no whitespace ambiguity. Emits the cleaned
// tree (blank titles dropped) via v-model so the parent can hand it to the
// import as the pre-seeded starting structure.
let uid = 0;
const nextKey = () => `k${++uid}`;

export default {
    name: 'kb-structure-editor',

    props: {
        // Initial seed: [{title, articles: [title, ...]}, ...]
        value: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            topics: this._hydrate(this.value),
        };
    },

    methods: {
        _hydrate(seed) {
            return (seed || []).map(t => ({
                key: nextKey(),
                title: t.title || '',
                articles: (t.articles || []).map(title => ({
                    key: nextKey(),
                    title: title || '',
                })),
            }));
        },
        addTopic() {
            this.topics.push({key: nextKey(), title: '', articles: []});
            this.emit();
        },
        removeTopic(ti) {
            this.topics.splice(ti, 1);
            this.emit();
        },
        addArticle(ti) {
            this.topics[ti].articles.push({key: nextKey(), title: ''});
            this.emit();
        },
        removeArticle(ti, ai) {
            this.topics[ti].articles.splice(ai, 1);
            this.emit();
        },
        // The emitted shape drops empty titles, so a half-filled row never
        // becomes a phantom topic/article in the import.
        emit() {
            const cleaned = this.topics
                .map(t => ({
                    title: t.title.trim(),
                    articles: t.articles
                        .map(a => a.title.trim())
                        .filter(Boolean),
                }))
                .filter(t => t.title);
            this.$emit('input', cleaned);
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-structure-editor {
    &__topic {
        margin-bottom: 14px;
        padding: 10px 12px;
        border: 1px solid #e2e2e2;
        border-radius: 6px;
        background: #fafafa;
    }

    &__topic-head {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__topic-input {
        font-weight: 600;
    }

    &__articles {
        margin: 8px 0 0 14px;
    }

    &__article {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 4px;
    }

    &__bullet {
        color: #aaa;
    }

    &__remove {
        color: #c00;
        flex-shrink: 0;
    }

    &__add-article {
        margin-top: 2px;
    }

    &__add-topic {
        margin-top: 4px;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-structure-editor.vue